/**
 * src/lib/voice/rollingNoiseFloor.js — TICKET-103 barge-in v2
 *
 * Pure exponential moving average (EMA) noise floor tracker for the barge-in
 * VAD. Replaces the one-shot 700ms calibration window from TICKET-101.
 *
 * Problem with one-shot calibration (proven by CEO-device telemetry):
 *   - 6/8 tests: calibration window fired BEFORE audio actually started →
 *     sampled silence → floor ≈ 0.02 → guard useless → assistant self-interrupted.
 *   - 2/8 tests: calibration window caught FULL speaker volume → threshold
 *     0.20-0.25 → no human voice can cross → assistant uninterruptible.
 *
 * Solution: continuous EMA that only adapts while TTS audio is genuinely
 * playing (sourceActive=true). When playback is silent between sentence chunks,
 * the floor is frozen at its last-known value so it doesn't drift toward 0.
 * A maxFloor cap prevents an implausibly loud TTS from producing an uncrossable
 * threshold (complements useVoiceBargeIn's maxThreshold clamp).
 *
 * Parameters:
 *   alpha    — EMA smoothing factor (0 < α ≤ 1). Larger = faster adaptation.
 *              Default 0.08 → ~12-frame time constant at 60fps (≈200ms).
 *   maxFloor — Hard cap on the floor value. Prevents runaway from an unusually
 *              loud speaker. Default 0.15.
 *
 * Usage:
 *   const rnf = createRollingNoiseFloor({ alpha: 0.08, maxFloor: 0.15 });
 *   // In each VAD tick:
 *   const floor = rnf.update(rms, sourceActive);
 *   // Or query without updating:
 *   const floor = rnf.getFloor();
 *   // On VAD stop/reset:
 *   rnf.reset();
 */

/**
 * @param {{ alpha?: number, maxFloor?: number }} opts
 * @returns {{ update(rms: number, sourceActive: boolean): number, getFloor(): number, reset(): void }}
 */
export function createRollingNoiseFloor({ alpha = 0.08, maxFloor = 0.15 } = {}) {
  if (alpha <= 0 || alpha > 1) throw new RangeError(`rollingNoiseFloor: alpha must be in (0, 1], got ${alpha}`);
  if (maxFloor <= 0) throw new RangeError(`rollingNoiseFloor: maxFloor must be positive, got ${maxFloor}`);

  let floor = 0;
  let seeded = false;

  /**
   * Update the EMA floor with a new RMS sample.
   * Only adapts when sourceActive=true (TTS is genuinely playing).
   * Returns the current floor value.
   *
   * @param {number} rms — RMS amplitude of the current audio frame [0, 1]
   * @param {boolean} sourceActive — true when an AudioBufferSource is playing
   */
  function update(rms, sourceActive) {
    if (!sourceActive) return floor; // frozen — playback not active, don't drift

    if (!seeded) {
      // First active sample seeds the floor directly so it isn't stuck at 0
      // for the first batch of frames.
      floor = Math.min(rms, maxFloor);
      seeded = true;
    } else {
      floor = Math.min(
        alpha * rms + (1 - alpha) * floor,
        maxFloor
      );
    }
    return floor;
  }

  /** Return the current floor without updating. */
  function getFloor() { return floor; }

  /** Clear accumulated state — call when barge-in is torn down between turns. */
  function reset() { floor = 0; seeded = false; }

  return { update, getFloor, reset };
}
